import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import isNil from 'lodash/isNil'
import { getAdminSettings } from 'enrollment-platform/utils/adminSettings'
import {
  GetAllUsersRequestDto,
  SortDirection,
  UserListSortField,
  type UserStatusBucket,
} from '../../api/dto/user-invitation.dto'
import { type UserScope } from './userDisplay'

const DEFAULT_PAGE_SIZE = 50
// Selectable page sizes. The backend caps `limit` at 100, so stay within that.
const PAGE_SIZE_OPTIONS = [20, 50, 100]

/**
 * Everything that narrows the roster, and the paging that walks it. Sole owner
 * of the request the table, the export and a select-all all send, so none of
 * them can cover a different set of users than the one on screen.
 */
export const useUsersFilters = () => {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [statusFilters, setStatusFilters] = useState<UserStatusBucket[]>([])
  const [scope, setScope] = useState<UserScope>('ALL')
  const [clientPublicKeys, setClientPublicKeys] = useState<string[]>([])
  // null leaves the column unfiltered; true/false keep the rows it answers
  // yes/no for.
  const [hasHrAdminAccess, setHasHrAdminAccess] = useState<boolean | null>(null)
  const [hasEmployeeAccess, setHasEmployeeAccess] = useState<boolean | null>(
    null
  )
  const [sortBy, setSortBy] = useState<UserListSortField | null>(null)
  const [sortDir, setSortDir] = useState<SortDirection | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [search])

  const { showTestData } = getAdminSettings().finance

  const trimmedSearch = debouncedSearch.trim()
  const hasSearch = trimmedSearch.length > 0

  const filters: GetAllUsersRequestDto = useMemo(
    () => ({
      search: hasSearch ? trimmedSearch : undefined,
      excludeTestClients: showTestData ? undefined : true,
      clientPublicKeys:
        clientPublicKeys.length > 0 ? clientPublicKeys : undefined,
      hasHrAdminAccess: hasHrAdminAccess ?? undefined,
      hasEmployeeAccess: hasEmployeeAccess ?? undefined,
      completeness: scope === 'ALL' ? undefined : scope,
      statuses: statusFilters.length > 0 ? statusFilters : undefined,
      sortBy: sortBy ?? undefined,
      sortDir: sortDir ?? undefined,
    }),
    [
      hasSearch,
      trimmedSearch,
      showTestData,
      clientPublicKeys,
      hasHrAdminAccess,
      hasEmployeeAccess,
      scope,
      statusFilters,
      sortBy,
      sortDir,
    ]
  )

  /** True when anything other than paging is narrowing the list. */
  const hasNarrowedList =
    hasSearch ||
    clientPublicKeys.length > 0 ||
    !isNil(hasHrAdminAccess) ||
    !isNil(hasEmployeeAccess) ||
    scope !== 'ALL' ||
    statusFilters.length > 0

  // An empty table is a dead end unless the thing that emptied it can be
  // undone from there, so this backs the action on the empty state.
  const handleClearFilters = useCallback(() => {
    setSearch('')
    setDebouncedSearch('')
    setClientPublicKeys([])
    setHasHrAdminAccess(null)
    setHasEmployeeAccess(null)
    setScope('ALL')
    setStatusFilters([])
    setPage(1)
  }, [])

  // The status funnel and the completeness dropdown read the same axis from two
  // ends, so only one is ever in force. Each option carries a count, and a
  // count is only true if picking it selects exactly the rows it counted:
  // Complete alongside "Pending registration" would promise 39 and show none.
  const handleStatusFilterChange = useCallback((value: UserStatusBucket[]) => {
    setStatusFilters(value)
    setScope('ALL')
    setPage(1)
  }, [])

  const handleScopeChange = useCallback((value: UserScope) => {
    setScope(value)
    setStatusFilters([])
    setPage(1)
  }, [])

  const handleCompaniesChange = useCallback((values: string[]) => {
    setClientPublicKeys(values)
    setPage(1)
  }, [])

  const handleHrAdminAccessChange = useCallback((value: boolean | null) => {
    setHasHrAdminAccess(value)
    setPage(1)
  }, [])

  const handleEmployeeAccessChange = useCallback((value: boolean | null) => {
    setHasEmployeeAccess(value)
    setPage(1)
  }, [])

  // Back to page 1, or a re-sort from deep in the list opens on rows nobody
  // asked for: the first page of the new order is the one being looked for.
  const handleSortChange = useCallback(
    (field: UserListSortField | null, dir: SortDirection | null) => {
      setSortBy(field)
      setSortDir(dir)
      setPage(1)
    },
    []
  )

  const handlePageSizeChange = useCallback((nextSize: number) => {
    setPageSize(nextSize)
    setPage(1)
  }, [])

  return {
    search,
    setSearch,
    page,
    setPage,
    pageSize,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    statusFilters,
    scope,
    clientPublicKeys,
    hasHrAdminAccess,
    hasEmployeeAccess,
    sortBy,
    sortDir,
    filters,
    hasNarrowedList,
    handleClearFilters,
    handleStatusFilterChange,
    handleScopeChange,
    handleCompaniesChange,
    handleHrAdminAccessChange,
    handleEmployeeAccessChange,
    handleSortChange,
    handlePageSizeChange,
  }
}
